import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { Plus, MessageSquare, Clock, CheckCircle, XCircle } from 'lucide-react';

const Support = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTicket, setNewTicket] = useState({ category: 'Other', title: '', message: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await api.get('/support/tickets');
      setTickets(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!newTicket.title || !newTicket.message) {
      alert('Please fill in all fields');
      return;
    }
    
    try {
      const response = await api.post('/support/tickets', newTicket);
      console.log('Ticket created:', response.data);
      alert('Ticket created successfully!');
      setShowCreateForm(false);
      setNewTicket({ category: 'Other', title: '', message: '' });
      fetchTickets();
    } catch (err) {
      console.error('Failed to create ticket:', err);
      alert(err.response?.data?.detail || 'Failed to create ticket. Please try logging in again.');
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'resolved' || status === 'closed') return <CheckCircle className="w-5 h-5 text-success" />;
    if (status === 'pending') return <Clock className="w-5 h-5 text-warning" />;
    return <MessageSquare className="w-5 h-5 text-primary" />;
  };

  if (loading) {
    return (
      <MobileLayout showBottomNav={false}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout showBottomNav={false}>
      <div className="p-4 space-y-4">
        <div>
          <button onClick={() => navigate(-1)} className="text-primary font-medium text-sm mb-2">← Back</button>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Support & Tickets</h1>
          <p className="text-sm text-gray-600 mt-1">Get help from our support team</p>
        </div>

        {/* Create Ticket Button */}
        <button onClick={() => setShowCreateForm(!showCreateForm)} className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2">
          <Plus className="w-5 h-5" />
          {showCreateForm ? 'Cancel' : 'Create New Ticket'}
        </button>

        {/* Create Form */}
        {showCreateForm && (
          <form onSubmit={handleCreateTicket} className="bg-white rounded-xl p-4 border border-gray-200 space-y-3">
            <h3 className="font-semibold text-gray-900">New Support Ticket</h3>
            <select value={newTicket.category} onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="KYC">KYC</option>
              <option value="Deposits">Deposits</option>
              <option value="Withdrawals">Withdrawals</option>
              <option value="Commissions">Commissions</option>
              <option value="Rewards">Rewards</option>
              <option value="Other">Other</option>
            </select>
            <input type="text" placeholder="Subject" value={newTicket.title} onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })} required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            <textarea placeholder="Describe your issue..." value={newTicket.message} onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })} required rows="4" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            <button type="submit" className="w-full bg-primary text-white font-semibold py-3 rounded-lg">Submit Ticket</button>
          </form>
        )}

        {/* Tickets List */}
        <div className="space-y-3">
          {tickets.length > 0 ? (
            tickets.map((ticket) => (
              <div key={ticket.id} className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-2">
                    {getStatusIcon(ticket.status)}
                    <div>
                      <h4 className="font-semibold text-sm text-gray-900">{ticket.title}</h4>
                      <p className="text-xs text-gray-600 mt-1">{ticket.message}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${ticket.status === 'open' ? 'bg-primary-light text-primary' : ticket.status === 'resolved' ? 'bg-success-light text-success' : 'bg-warning-light text-warning'}`}>
                    {ticket.status.toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-500">{ticket.category}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-lg">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-600">No support tickets yet</p>
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
};

export default Support;